import { Injectable } from '@angular/core';
import { RequestModel } from '@bo-client/shared/models/request.model';
import { RequestFormPort } from './request-form-port';
import { ActionWithMoreInfoModel } from '@bo-client/activation-management/shared/models/actionWithMoreInfo.model';
import { ShowErrorModel } from '@bo-client/shared/form-errors/errors-form-model';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatDialogSize } from '@bo-client/shared/constants/mat-dialog-size';
import { GenericConfirmationModalComponent } from '@bo-client/shared/modals/generic-confirmation-modal/generic-confirmation-modal.component';
import { ConfimWithCommentModalComponent } from '@bo-client/activation-management/commissioning/forms/components/modal-validate-commissioning/confim-with-comment-modal.component';
import { CustomFormModalComponent } from '@bo-client/activation-management/shared/components/custom-form-modal/custom-form-modal.component';
import { SecondActionTypes } from '@bo-client/activation-management/shared/models/actionWithMoreInfo.model';
import { ToastrService } from 'ngx-toastr';
import { HttpHapService } from '@bo-client/core/http/http-hap.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { RequestApprovalDto } from '@bo-client/activation-management/grid-registration/model/RequestApprovalDto';

export interface RequestPageContext<T extends RequestModel> {
  form: RequestFormPort<T>;
  model: T;
  saveUrl: string;
  compUnsub$: Subject<void>;
  matDialog: MatDialog;
  httpService: HttpHapService;
  toastService: ToastrService;
  router: Router;
  updateMainForm(result: T): void;
}

@Injectable({
  providedIn: 'root'
})
export class PageActionExecutorService {
  executeAction<T extends RequestModel>(
    context: RequestPageContext<T>,
    action: ActionWithMoreInfoModel<T>
  ): void {
    if (action.validateForm) {
      if (!context.form.isValid()) {
        context.toastService.error(
          context.form
            .getAllValidationErrors()
            .map((errorModel) => ShowErrorModel.toReadableString(errorModel))
            .join('<br/>'),
          'Bitte überprüfen Sie Ihre Eingaben für folgende Felder:',
          {
            enableHtml: true
          }
        );
        return;
      }
    }

    // Bisherige String-basierte Execute-Strategie weiterverwenden,
    // aber zentral über diesen Service routen.
    if (!action.execute || typeof context[action.execute] !== 'function') {
      // Fallback: direkter Aufruf der bekannten Methoden auf dem Kontext.
      switch (action.execute) {
        case 'save':
          this.save(context, action);
          return;
        case 'saveAndActionPlusMail':
          this.saveAndActionPlusMail(context, action);
          return;
        case 'remove':
          this.remove(context, action);
          return;
      }
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore – erlaubt bestehende Implementierungen auf der Page weiterzuverwenden
    context[action.execute](action);
  }

  confirmActionWithComment<T extends RequestModel>(
    context: RequestPageContext<T>,
    actionInfo: ActionWithMoreInfoModel<T>
  ): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.id = 'commission-request-activity';
    dialogConfig.maxHeight = '100%';
    dialogConfig.width = MatDialogSize.width;
    dialogConfig.data = actionInfo;

    context.matDialog
      .open(ConfimWithCommentModalComponent, dialogConfig)
      .afterClosed()
      .pipe(takeUntil(context.compUnsub$))
      .subscribe((res) => {
        if (res) {
          this.saveAndActionPlusMail(context, actionInfo);
        }
      });
  }

  confirmAction<T extends RequestModel>(
    context: RequestPageContext<T>,
    actionInfo: ActionWithMoreInfoModel<T>
  ): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.id = 'confirm-action';
    dialogConfig.maxHeight = MatDialogSize.maxHeightLogin;
    dialogConfig.width = MatDialogSize.width;
    dialogConfig.data = {
      title: actionInfo.actionName,
      message: 'Bitte bestätigen Sie, dass Sie die Aktion ausführen möchten',
      confirmText: 'Bestätigen',
      cancelText: 'Abbrechen'
    };
    context.matDialog
      .open(GenericConfirmationModalComponent, dialogConfig)
      .afterClosed()
      .pipe(takeUntil(context.compUnsub$))
      .subscribe((res) => {
        if (res) {
          this.saveAndActionPlusMail(context, actionInfo);
        }
      });
  }

  customFormWithCustomActionModal<T extends RequestModel>(
    context: RequestPageContext<T>,
    actionInfo: ActionWithMoreInfoModel<T>
  ): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.id = 'custom-form-with-custom-action';
    dialogConfig.maxHeight = MatDialogSize.maxHeight;
    dialogConfig.width = MatDialogSize.largeWidth;
    dialogConfig.data = {
      actionInfo: actionInfo,
      model: context.model
    };

    context.matDialog
      .open(CustomFormModalComponent, dialogConfig)
      .afterClosed()
      .pipe(takeUntil(context.compUnsub$))
      .subscribe((customFormResponse) => {
        if (customFormResponse) {
          if (Object.prototype.hasOwnProperty.call(customFormResponse, 'additionalData')) {
            actionInfo.additionalData = customFormResponse.additionalData;
            actionInfo.comment = customFormResponse.comment;
          } else {
            context.model = customFormResponse;
          }
          switch (actionInfo.secondAction) {
            case SecondActionTypes.saveModel:
              this.save(context, actionInfo, context.model);
              return;
            case SecondActionTypes.saveModelAndActionPlusMail:
              this.saveAndActionPlusMail(context, actionInfo, context.model);
              return;
          }
        }
      });
  }

  saveAndActionPlusMail<T extends RequestModel>(
    context: RequestPageContext<T>,
    actionData: ActionWithMoreInfoModel<T>,
    model: T = null
  ): void {
    if (!model) {
      model = context.form.getModelFromForm();
    }

    if (actionData.preActionHook) {
      actionData.preActionHook(actionData, model);
    }

    context.httpService
      .putOrPost(model, context.saveUrl)
      .pipe(takeUntil(context.compUnsub$))
      .subscribe(
        (result) => {
          context.toastService.success('Speichern erfolgreich');

          context.form.updateFormWithModel(result);

          const requestApprovalDto: RequestApprovalDto = {
            id: result.id,
            subject: actionData.subject,
            comment: actionData.comment
          };

          if (actionData.additionalData) {
            requestApprovalDto.additionalData = actionData.additionalData;
          }

          this.actionPlusMail(context, requestApprovalDto, actionData);
        },
        (error) => {
          this.displayErrorMessage(context, error, actionData.failureMessage);
        }
      );
  }

  save<T extends RequestModel>(
    context: RequestPageContext<T>,
    actionInfo: ActionWithMoreInfoModel<T>,
    model: T = null
  ): void {
    if (!model || !(model instanceof RequestModel)) {
      model = context.form.getModelFromForm();
    }

    context.httpService
      .putOrPost(model, context.saveUrl)
      .pipe(takeUntil(context.compUnsub$))
      .subscribe(
        (result) => {
          context.toastService.success('Speichern erfolgreich');
          context.updateMainForm(result);
        },
        (error) => {
          this.displayErrorMessage(context, error, 'Beim Speichern ist ein Fehler aufgetreten');
        }
      );
  }

  remove<T extends RequestModel>(
    context: RequestPageContext<T>,
    actionInfo: ActionWithMoreInfoModel<T>,
    model: T = null
  ): void {
    if (!model || !(model instanceof RequestModel)) {
      model = context.form.getModelFromForm();
    }

    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.id = 'confirm-delete-action';
    dialogConfig.maxHeight = MatDialogSize.maxHeightLogin;
    dialogConfig.width = MatDialogSize.width;
    dialogConfig.data = {
      title: actionInfo.actionName,
      message:
        'Das Löschen eines Antrages kann nicht rückgängig gemacht werden! Sind Sie wirklich sicher, dass sie den Antrag mit der ID ' +
        model.id +
        ' wirklich löschen möchten?',
      confirmText: 'Ja, ich bin mir sicher',
      cancelText: 'Abbrechen'
    };
    context.matDialog
      .open(GenericConfirmationModalComponent, dialogConfig)
      .afterClosed()
      .pipe(takeUntil(context.compUnsub$))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        context.httpService
          .delete(actionInfo.actionUrl + model.id.toString())
          .pipe(takeUntil(context.compUnsub$))
          .subscribe({
            next: () => {
              const redirectUrl = actionInfo.additionalData?.redirectOnSuccessUrl || null;
              context.toastService.success('Löschen erfolgreich');
              if (redirectUrl) {
                context.router.navigate([redirectUrl]);
              }
            },
            error: (error) => {
              console.error('Error deleting entity', error, actionInfo, model);
              context.toastService.error('Fehler beim Löschen des Antrags mit ID ' + model.id);
            }
          });
      });
  }

  private actionPlusMail<T extends RequestModel>(
    context: RequestPageContext<T>,
    gridRegistrationDto: RequestApprovalDto,
    actionInfo: ActionWithMoreInfoModel<T>
  ): void {
    context.httpService
      .post(gridRegistrationDto, actionInfo.actionUrl)
      .pipe(takeUntil(context.compUnsub$))
      .subscribe(
        (result) => {
          context.toastService.success(actionInfo.successMessage);
          context.updateMainForm(result);
        },
        (error) => {
          this.displayErrorMessage(context, error, actionInfo.failureMessage);
        }
      );
  }

  private displayErrorMessage<T extends RequestModel>(
    context: RequestPageContext<T>,
    error: any,
    defaultMessage: string
  ): void {
    if (error?.error?.message) {
      context.toastService.error(error.error.message);
    } else {
      context.toastService.error(defaultMessage);
    }
  }
}
